import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Collapse, Divider, Stack, Switch, TextField, Typography } from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { SectionCard, StatusChip } from '../ui';
import { paymentService, type PaymentProvider, type ProviderTest } from '../../services/paymentService';
import { QrUpload } from './QrUpload';

interface FieldDef {
  key: string;
  label: string;
  hint: string;
  required?: boolean;
  /** Tiêu đề nhóm — field đầu của mỗi nhóm mang tiêu đề, các field sau để trống. */
  group?: string;
  /**
   * Hiện nhưng KHÔNG cho sửa, và không gửi lên khi lưu.
   *
   * Dành cho hằng số kỹ thuật đã đúng sẵn từ migration, mà gõ sai một ký tự là
   * hỏng lặng lẽ: sai `contract` thì quét nhầm token và không giao dịch nào khớp;
   * sai `decimals` thì lệch tiền 10^12 lần. Không có lý do vận hành nào để sửa
   * chúng qua giao diện.
   */
  readOnly?: boolean;
  /** Gập vào mục "Nâng cao" — hiếm khi cần đụng tới. */
  advanced?: boolean;
  /** Hiện dạng công tắc on/off; lưu chuỗi 'true'/'false'. */
  bool?: boolean;
  /** Giá trị mặc định khi config chưa có field này (dùng cho công tắc). */
  defaultOn?: boolean;
}

/**
 * Field cấu hình theo `kind` — phải khớp CONFIG_FIELDS ở server.
 *
 * Server từ chối tên lạ bằng 422, nên lệch ở đây hiện thành lỗi lưu chứ không
 * âm thầm mất dữ liệu. Nhưng THIẾU ở đây thì không có lỗi nào cả: field chỉ đơn
 * giản không xuất hiện và admin không cấu hình nổi. Thêm field ở server thì phải
 * thêm ở đây — danh sách này là bản sao thủ công của bên kia.
 */
const CONFIG_FIELDS: Record<string, FieldDef[]> = {
  binance_p2p: [
    { key: 'binanceId', label: 'Binance ID', hint: 'Pay ID của tài khoản nhận tiền', required: true },
    { key: 'binanceName', label: 'Tên người nhận', hint: 'Khách đối chiếu trước khi chuyển' },
    { key: 'instructions', label: 'Hướng dẫn thêm', hint: 'Dòng chữ phụ hiện dưới phần thanh toán' },
  ],
  chain: [
    // ── Tiền VÀO: cấu hình mạng để nhận và đối soát ──
    {
      group: 'Nhận tiền',
      key: 'networkLabel',
      label: 'Tên mạng hiện cho khách',
      hint: 'Ví dụ: BNB Smart Chain (BEP20)',
    },
    {
      key: 'network',
      label: 'Mã mạng (nội bộ)',
      hint: 'Khoá chống xử lý trùng giao dịch được dựng từ giá trị này — đổi là cộng điểm hai lần',
      readOnly: true,
    },
    {
      key: 'chainId',
      label: 'Chain ID',
      hint: 'Sai số này là ký giao dịch sai mạng và quét log nhầm chain',
      readOnly: true,
    },
    { key: 'contract', label: 'Hợp đồng USDT', hint: 'Cố định theo mạng', readOnly: true },
    { key: 'decimals', label: 'Số lẻ (decimals)', hint: 'Cố định theo mạng', readOnly: true },
    { key: 'instructions', label: 'Hướng dẫn thêm', hint: 'Dòng chữ phụ hiện dưới phần thanh toán' },

    // ── Tiền RA: gom về ví của bạn ──
    {
      group: 'Gom tiền về',
      key: 'sweepTo',
      label: 'Ví đích — tiền gom về đây',
      hint: 'Địa chỉ 0x… Khoá của ví này KHÔNG được nằm trong hệ thống, nếu không gom xong vẫn mất khi server bị chiếm',
      required: true,
    },
    {
      key: 'gasFunderIndex',
      label: 'Chỉ số HD của ví trả gas',
      hint: 'Cố định 2147483647 — nằm ngoài dãy cấp cho khách. Đặt số thấp là địa chỉ khách đó biến mất khỏi bảng gom tiền mà không cảnh báo gì. Địa chỉ ví hiện ở tab Gom tiền',
      readOnly: true,
    },
    {
      key: 'gasMin',
      label: 'Gas tối thiểu mỗi ví',
      hint: 'Sàn tĩnh, ví dụ 0.0005. Nhu cầu thật tính theo giá mạng hiện tại, số này chỉ là mức sàn',
    },
    { key: 'gasFund', label: 'Gas bơm mỗi lần', hint: 'Ví dụ 0.001' },
    {
      key: 'sweepAlertUsdt',
      label: 'Ngưỡng cảnh báo (USDT)',
      hint: 'Tiền chưa gom vượt mức này thì cron ghi cảnh báo và tab Gom tiền hiện băng vàng',
    },
    {
      key: 'autoSweep',
      label: 'Gom tự động theo cron',
      hint: 'Tắt thì cron KHÔNG tự gom; vẫn gom tay được ở tab Gom tiền. Vẫn giữ ví đích.',
      bool: true,
      defaultOn: true,
    },

    // ── Hiếm khi đụng tới ──
    {
      advanced: true,
      key: 'minConfirmations',
      label: 'Số xác nhận tối thiểu',
      hint: 'Chỉ cộng điểm cho block đã đủ sâu, tránh cộng dựa trên block bị đảo',
    },
    { advanced: true, key: 'explorerTx', label: 'Tiền tố link explorer', hint: 'Ví dụ: https://bscscan.com/tx/' },
    {
      advanced: true,
      key: 'rpcUrls',
      label: 'RPC riêng',
      hint: 'Nhiều URL cách nhau bằng dấu phẩy. Để trống thì dùng danh sách RPC công cộng',
    },
    {
      advanced: true,
      key: 'maxRpcLookback',
      label: 'Lùi tối đa khi chỉ có RPC',
      hint: 'Mặc định 4000 block — node công cộng không phục vụ lịch sử sâu hơn',
    },
  ],
};

/**
 * Field server cho phép nhưng CỐ Ý không hiện trên form.
 *
 * `qrImageUrl` (dán link ảnh QR) bị thay bởi nút tải ảnh `QrUpload`: ảnh tải lên
 * luôn thắng và server xoá `qrImageUrl` khi nhận ảnh. Hiện thêm ô dán link chỉ
 * tạo hai nguồn cho cùng một ảnh. Ẩn ở đây thì field không lọt vào nhóm
 * "Chưa phân loại" và không bị gửi lên khi lưu — server gộp config nên giá trị
 * cũ (nếu có) vẫn giữ nguyên.
 */
const HIDDEN_FIELDS: Record<string, string[]> = {
  binance_p2p: ['qrImageUrl'],
};

const SECRET_FIELDS: Record<string, Array<{ key: string; label: string; hint: string }>> = {
  binance_pay: [
    { key: 'pushKey', label: 'Push key', hint: 'Chuỗi tối thiểu 16 ký tự, phải TRÙNG BINANCE_PUSH_KEY của poller' },
  ],
};

interface Props {
  provider: PaymentProvider;
  /** Allowlist server trả về. Field nào ở đây mà trang chưa có nhãn vẫn được hiện. */
  allowedKeys: string[];
  onSaved: () => void;
  onToast: (msg: string, sev: 'success' | 'error') => void;
}

export const GatewayCard: React.FC<Props> = ({ provider, allowedKeys, onSaved, onToast }) => {
  const known = CONFIG_FIELDS[provider.kind] ?? [];
  const hidden = HIDDEN_FIELDS[provider.kind] ?? [];
  // Field server cho phép mà trang chưa có nhãn — hiện dạng thô thay vì bỏ sót.
  // Thiếu một field là lỗi IM LẶNG: không có gì báo, admin chỉ đơn giản không
  // cấu hình được thứ đó (đúng chuyện đã xảy ra với `sweepTo`).
  // Ghim theo NỘI DUNG của allowedKeys, không theo tham chiếu mảng: mỗi lần tải
  // lại provider là một mảng mới, và nếu không ghim thì hiệu ứng nạp giá trị ban
  // đầu bên dưới hoặc chạy vô hạn, hoặc dùng phải bản cũ.
  const allowedSig = allowedKeys.join(',');
  const unlabelled: FieldDef[] = useMemo(
    () =>
      allowedSig
        .split(',')
        .filter((k) => k && !hidden.includes(k) && !known.some((f) => f.key === k))
        .map((k, i) => ({
          key: k,
          label: k,
          hint: 'Server cho phép field này nhưng trang chưa có mô tả tiếng Việt.',
          ...(i === 0 ? { group: 'Chưa phân loại' } : {}),
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowedSig, provider.kind],
  );
  const fields = [...known, ...unlabelled];
  const secretDefs = SECRET_FIELDS[provider.id] ?? [];

  const [config, setConfig] = useState<Record<string, string>>({});
  const [secretInput, setSecretInput] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(provider.enabled);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<ProviderTest | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Nạp lại mỗi khi bản từ server đổi. Thiếu bước này thì sau khi lưu, ô nhập vẫn
  // giữ chữ cũ và không ai biết bản đang chạy trên server giờ là gì.
  useEffect(() => {
    const next: Record<string, string> = {};
    for (const f of [...(CONFIG_FIELDS[provider.kind] ?? []), ...unlabelled]) {
      next[f.key] = provider.config?.[f.key] ?? (f.bool ? (f.defaultOn ? 'true' : 'false') : '');
    }
    setConfig(next);
    setSecretInput({});
    setEnabled(provider.enabled);
    setTest(null);
  }, [provider, unlabelled]);

  const save = async () => {
    setSaving(true);
    try {
      // Chỉ gửi bí mật người dùng thật sự gõ. Ô để trống nghĩa là giữ nguyên khoá
      // cũ — không gửi field đó lên là cách diễn đạt rõ nhất của ý ấy.
      const typed = Object.fromEntries(Object.entries(secretInput).filter(([, v]) => v.trim().length > 0));
      // Field readOnly KHÔNG gửi lên: không gửi nghĩa là server giữ nguyên. Gửi
      // kèm chỉ tạo thêm một đường để chúng bị đổi do lỗi giao diện.
      const editableKeys = new Set(fields.filter((f) => !f.readOnly).map((f) => f.key));
      const payload = Object.fromEntries(Object.entries(config).filter(([k]) => editableKeys.has(k)));
      await paymentService.saveProvider({
        id: provider.id,
        enabled,
        config: payload,
        ...(Object.keys(typed).length ? { secrets: typed } : {}),
      });
      onToast(`Đã lưu cổng ${provider.displayName}`, 'success');
      setSecretInput({});
      onSaved();
    } catch (e) {
      onToast((e as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const clearSecret = async (key: string) => {
    setSaving(true);
    try {
      await paymentService.saveProvider({ id: provider.id, secrets: { [key]: null } });
      onToast(`Đã xoá ${key}. Webhook sẽ từ chối mọi lượt đẩy tới khi đặt khoá mới.`, 'success');
      onSaved();
    } catch (e) {
      onToast((e as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      setTest(await paymentService.testProvider(provider.id));
    } catch (e) {
      onToast((e as Error).message, 'error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <SectionCard
      title={provider.displayName}
      description={`${provider.id} · ${provider.kind} · ${provider.currency}`}
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          <StatusChip
            label={provider.enabled ? 'Đang bật' : 'Đang tắt'}
            tone={provider.enabled ? 'success' : 'neutral'}
          />
          <Button size="small" startIcon={<ScienceOutlinedIcon />} onClick={() => void runTest()} disabled={testing}>
            Kiểm tra
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            onClick={() => void save()}
            disabled={saving}
          >
            Lưu
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2.5}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <Box>
            <Typography sx={{ fontWeight: 600 }}>Bật cổng này cho khách</Typography>
            <Typography variant="body2" color="text.secondary">
              {provider.kind === 'chain'
                ? 'Cổng on-chain quét bằng cron mỗi phút. Địa chỉ nhận dẫn xuất từ secret EVM_XPUB — server chỉ giữ khoá CÔNG KHAI, không ký được giao dịch nào, nên việc gom tiền phải chạy ngoài.'
                : 'Cổng tắt thì trang /topup không hiện phương thức nào. Server không cho bật khi chưa có Binance ID — bật một cổng rỗng là hiện ô trống rồi chờ khoản tiền không bao giờ tới.'}
            </Typography>
          </Box>
        </Stack>

        <Divider />

        {fields.filter((f) => !f.advanced).map((f) => {
          // Ví đích là thứ quyết định tiền đi đâu — cảnh báo ngay tại ô nhập nếu
          // sai định dạng, đừng để phát hiện lúc bấm Gom.
          const badAddress =
            f.key === 'sweepTo' && !!config[f.key]?.trim() && !/^0x[0-9a-fA-F]{40}$/.test(config[f.key].trim());
          return (
            <Box key={f.key} sx={{ width: '100%' }}>
              {f.group && (
                <Typography
                  variant="overline"
                  sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, mb: 0.5, mt: 1 }}
                >
                  {f.group}
                </Typography>
              )}
              {f.bool ? (
                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                  <Switch
                    checked={(config[f.key] ?? '').toLowerCase() !== 'false'}
                    onChange={(e) => setConfig((c) => ({ ...c, [f.key]: e.target.checked ? 'true' : 'false' }))}
                  />
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{f.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {f.hint}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
              <TextField
                label={f.required ? `${f.label} *` : f.label}
                helperText={badAddress ? 'Phải là địa chỉ 0x… đúng 40 ký tự hex' : f.hint}
                error={badAddress}
                value={config[f.key] ?? ''}
                onChange={(e) => setConfig((c) => ({ ...c, [f.key]: e.target.value }))}
                size="small"
                fullWidth
                disabled={f.readOnly}
              />
              )}
            </Box>
          );
        })}

        {fields.some((f) => f.advanced) && (
          <Box>
            <Button
              size="small"
              onClick={() => setShowAdvanced((v) => !v)}
              startIcon={showAdvanced ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
              sx={{ color: 'text.secondary' }}
            >
              Nâng cao
            </Button>
            <Collapse in={showAdvanced}>
              <Stack spacing={2} sx={{ mt: 1.5 }}>
                {fields
                  .filter((f) => f.advanced)
                  .map((f) => (
                    <TextField
                      key={f.key}
                      label={f.label}
                      helperText={f.hint}
                      value={config[f.key] ?? ''}
                      onChange={(e) => setConfig((c) => ({ ...c, [f.key]: e.target.value }))}
                      size="small"
                      fullWidth
                    />
                  ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {provider.kind === 'binance_p2p' && (
          <>
            <Divider />
            <QrUpload provider={provider} onChanged={onSaved} onToast={onToast} />
          </>
        )}

        {secretDefs.length > 0 && <Divider />}

        {secretDefs.map((s) => {
          const st = provider.secrets?.[s.key];
          return (
            <Stack key={s.key} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
              <TextField
                label={s.label}
                type="password"
                autoComplete="new-password"
                size="small"
                fullWidth
                value={secretInput[s.key] ?? ''}
                onChange={(e) => setSecretInput((v) => ({ ...v, [s.key]: e.target.value }))}
                placeholder={st?.set ? `đã đặt (…${st.fingerprint}) — để trống = giữ nguyên` : 'Chưa đặt'}
                helperText={s.hint}
              />
              {st?.set && (
                <Button size="small" color="error" onClick={() => void clearSecret(s.key)} disabled={saving}>
                  Xoá khoá
                </Button>
              )}
            </Stack>
          );
        })}

        {test && (
          <Alert
            severity={test.ready ? 'success' : 'warning'}
            icon={test.ready ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
          >
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
              {test.ready ? 'Cổng đã đủ điều kiện nhận tiền' : `Còn thiếu: ${test.blocking.join(', ')}`}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {test.checks.map((c) => (
                <Typography key={c.key} component="li" variant="body2">
                  {c.ok ? '✓' : '✗'} <b>{c.key}</b> — {c.detail}
                </Typography>
              ))}
            </Box>
          </Alert>
        )}
      </Stack>
    </SectionCard>
  );
};
